
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const { t } = useTranslation('about');
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleScroll = () => {
      if (!prefersReducedMotion) {
        setScrollY(window.scrollY);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 100);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [prefersReducedMotion]);

  const parallaxOffset = prefersReducedMotion ? 0 : Math.min(scrollY * 0.4, 24);
  const gridOffset = prefersReducedMotion ? 0 : Math.min(scrollY * 0.8, 24);

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Background Image with Parallax */}
      <div 
        className="absolute inset-0 w-full h-full"
        style={{
          transform: prefersReducedMotion ? 'none' : `translateY(${parallaxOffset}px)`,
          willChange: prefersReducedMotion ? 'auto' : 'transform'
        }}
      >
        <img
          src="https://public.readdy.ai/ai/img_res/5a55e432e500b034718252288f7b396d.jpg"
          alt="Energy Infrastructure"
          className="w-full h-full object-cover object-top"
          style={{
            filter: 'saturate(0.8) brightness(0.7)',
            transform: prefersReducedMotion ? 'scale(1)' : 'scale(1.02)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A]/80 via-[#0D1B2A]/60 to-transparent"></div>
      </div>

      {/* Decorative Grid Lines */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          transform: prefersReducedMotion ? 'none' : `translateY(${gridOffset}px)`,
          willChange: prefersReducedMotion ? 'auto' : 'transform'
        }}
      >
        <div className="w-full h-full" style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div 
              className={`transition-all duration-700 ease-out ${
                isVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                {t('hero.title')}
                <span className="block text-[#2E8BC0]">{t('hero.titleHighlight')}</span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-[#F5F7FA] mb-8 leading-relaxed max-w-2xl uppercase font-thin">
                {t('hero.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
        <i className="ri-arrow-down-line text-2xl"></i>
      </div>
    </section>
  );
}
